// src/components/BulkExportDialog.js
import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    Alert,
    Typography,
    Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import plLocale from 'date-fns/locale/pl';
import { FileDownload as DownloadIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || '';

const BulkExportDialog = ({ open, onClose }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [documentType, setDocumentType] = useState('');
    const [limit, setLimit] = useState(1000);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Reset stanu przy otwarciu dialogu
    const handleOnEnter = () => {
        setStartDate(null);
        setEndDate(null);
        setDocumentType('');
        setLimit(1000);
        setError(null);
    };

    const handleExport = async () => {
        setLoading(true);
        setError(null);

        try {
            // Budowanie parametrów zapytania
            const queryParams = new URLSearchParams();

            if (startDate) {
                queryParams.append('startDate', startDate.toISOString().split('T')[0]);
            }

            if (endDate) {
                queryParams.append('endDate', endDate.toISOString().split('T')[0]);
            }

            if (documentType) {
                queryParams.append('documentType', documentType);
            }

            if (limit) {
                queryParams.append('limit', limit.toString());
            }

            // Pobierz URL z backendu
            const url = `${API_URL}/api/expenses/export-csv?${queryParams.toString()}`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Błąd pobierania CSV');
            }

            // Pobierz zawartość CSV
            const blob = await response.blob();

            // Stwórz link i symuluj kliknięcie
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;

            // Generuj nazwę pliku z datami, jeśli wybrane
            let fileName = 'dokumenty';
            if (startDate && endDate) {
                fileName += `_${startDate.toISOString().split('T')[0]}_do_${endDate.toISOString().split('T')[0]}`;
            } else if (startDate) {
                fileName += `_od_${startDate.toISOString().split('T')[0]}`;
            } else if (endDate) {
                fileName += `_do_${endDate.toISOString().split('T')[0]}`;
            }
            fileName += '.csv';

            a.download = fileName;
            document.body.appendChild(a);
            a.click();

            // Posprzątaj
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);

            // Zamknij dialog po pomyślnym pobraniu
            onClose();
        } catch (err) {
            console.error('Error downloading CSV:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !loading && onClose()}
            fullWidth
            maxWidth="md"
            TransitionProps={{
                onEnter: handleOnEnter
            }}
        >
            <DialogTitle>Eksport dokumentów do CSV</DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" paragraph>
                        Wybierz kryteria dla dokumentów, które chcesz wyeksportować. Możesz filtrować według daty, typu dokumentu i ograniczyć liczbę eksportowanych dokumentów.
                    </Typography>

                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
                                <DatePicker
                                    label="Data początkowa"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={loading}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
                                <DatePicker
                                    label="Data końcowa"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                    disabled={loading}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="document-type-label">Typ dokumentu</InputLabel>
                                <Select
                                    labelId="document-type-label"
                                    value={documentType}
                                    onChange={(e) => setDocumentType(e.target.value)}
                                    label="Typ dokumentu"
                                    disabled={loading}
                                >
                                    <MenuItem value="">Wszystkie</MenuItem>
                                    <MenuItem value="invoice">Faktury</MenuItem>
                                    <MenuItem value="receipt">Paragony</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Limit dokumentów"
                                type="number"
                                value={limit}
                                onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 0))}
                                fullWidth
                                helperText="Maksymalna liczba dokumentów do eksportu"
                                InputProps={{ inputProps: { min: 1, max: 10000 } }}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={loading}
                    color="inherit"
                >
                    Anuluj
                </Button>
                <Button
                    onClick={handleExport}
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                >
                    {loading ? 'Eksportowanie...' : 'Eksportuj'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkExportDialog;